import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { useScreenTab } from '../../shared/screen-tab.ts'
import { useTranslation } from 'react-i18next'
import { formatVND, toMoney } from '@rs/contracts'
import { Button, Card, Modal } from '@rs/ui'
import { loadKeyGroups } from '@rs/ui/i18n'
import { qk, useList, useOne } from '../../shared/queries.ts'
import { ApiError } from '../../shared/api.ts'
import { useAuth } from '../../app/auth.tsx'
import { fetchMyLegs, fetchMyPlanned, fetchMyVendorCard, rejectMyLegApi, type MyLegRow } from './api.ts'
import { fetchMyApInvoices } from './recon-api.ts'
import { BiddingTab } from './BiddingTab.tsx'
import { fetchMyBids } from '../bidding/api.ts'
import { OrdersTab } from './OrdersTab.tsx'
import { PlanTab } from './PlanTab.tsx'
import { ReportTab } from './ReportTab.tsx'
import { ReconTab } from './ReconTab.tsx'

/**
 * ══ CỔNG THẦU PHỤ (F13) — chép `NccPortal.tsx` (`proship-trans/src/pages/NccPortal.tsx:360-1032`) ══
 *
 * ⭐ KHUNG RIÊNG, không có sidebar/menu công ty (bản cũ user 17/07): thanh tiêu đề tím (logo · "Cổng
 *   thầu phụ" · tên NCC · MST · số máy · Đăng xuất) → ba thẻ tổng quan → năm tab theo đúng thứ tự
 *   bản cũ: 📋 Kế hoạch · 📦 Đơn hàng của tôi · 📣 Chào giá · 🧾 Đối soát cước · 📊 Báo cáo.
 * ⭐ THẺ "CƯỚC CÒN ĐƯỢC NHẬN" hiện ở MỌI tab như bản cũ (`NccPortal.tsx:450-461`) — đó là công nợ CỦA
 *   CHÍNH thầu phụ (cùng con số với tab Đối soát), không phải giá vốn hay giá bán của đơn. Chốt C23 ①
 *   theo hướng (a) ngày 25/09/2026: trả ô tiền về như bản cũ.
 * ⚠ Tab Đơn hàng vẫn KHÔNG có ô tiền nào — `VendorLegDto` không mang giá của đơn.
 */

const TABS = ['plan', 'orders', 'bidding', 'recon', 'report'] as const
type Tab = (typeof TABS)[number]

export const VendorPortalPage = (): JSX.Element => {
  const { t } = useTranslation()
  const { me, logout } = useAuth()
  const [ready, setReady] = useState(false)
  const [tab, setTab, tabShown] = useScreenTab<Tab>('vendorPortal', { fallback: 'orders' })
  const [err, setErr] = useState<string | null>(null)
  const [rejectOf, setRejectOf] = useState<MyLegRow | null>(null)
  const [reason, setReason] = useState('')

  const { rows, err: loadErr, reload } = useList(qk.myLegs(), fetchMyLegs)
  // Cùng khoá cache với từng tab — mở tab không gọi lại cửa đã gọi để đếm.
  const { rows: bids } = useList(qk.myBids(), fetchMyBids)
  const { rows: planned, reload: reloadPlanned } = useList(qk.myPlanned(), fetchMyPlanned)
  const { data: recon } = useOne(qk.myApInvoices(), fetchMyApInvoices)
  const { data: card, err: cardErr } = useOne(qk.myVendorCard(), fetchMyVendorCard)

  useEffect(() => {
    let alive = true
    // `azOps` cho lịch 📋 Kế hoạch (khoá `azOps.vendorPlan.*` dùng chung với màn Điều hành). Thiếu
    // nhóm nào là MÀN TRẮNG — cổng `tools/i18n-group-loaded.test.ts` canh chuyện đó.
    void loadKeyGroups('ncc', 'az', 'azOps', 'sales', 'app', 'common').then(() => { if (alive) setReady(true) })
    return () => { alive = false }
  }, [])

  if (!ready) return <p>{t('common.loading')}</p>

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setErr(null)
    try { await fn(); setRejectOf(null); setReason(''); reload() }
    catch (e) { setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e)) }
  }

  const legs = rows ?? []
  const pending = legs.filter((l) => !l.vendorConfirmed && !l.vendorRejected).length
  /* Tài khoản chưa nối NCC ⇒ máy chủ trả 403 cho MỌI cửa của cổng. Bản cũ hiện một thẻ nhắc duy nhất
     thay cho cả cổng (`NccPortal.tsx:452`), không bày năm tab rỗng. */
  const noVendor = cardErr !== null && card === null
  const tabLabel: Record<Tab, string> = {
    plan: t('ncc.tabPlan', { n: (planned ?? []).length }),
    orders: t('ncc.tabOrders', { n: legs.length }),
    bidding: t('ncc.tabBidding', { n: (bids ?? []).length }),
    recon: t('ncc.tabRecon', { n: recon?.rows.length ?? 0 }),
    report: t('ncc.tabReport'),
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Thanh tiêu đề cổng NCC — KHÔNG có sidebar/menu công ty. */}
      <header className="bg-gradient-to-r from-violet-600 to-brand-500 text-white">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/RT-LOGO-header.jpg" alt={t('app.name')}
                 className="h-9 w-auto bg-white rounded-lg px-1.5 py-1 object-contain" />
            <div className="min-w-0">
              <h2 className="text-[11px] text-violet-100 uppercase tracking-wide font-semibold">{t('ncc.portal')}</h2>
              <div className="font-bold truncate max-w-[60vw]">{card?.vendorName ?? (noVendor ? t('ncc.noVendor') : me?.name ?? '')}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {card !== null && (
              <span className="text-[11px] bg-white/15 rounded-lg px-2 py-1 hidden sm:block">
                {/* `||` như bản cũ (`NccPortal.tsx:434`, V04110): MST/ĐT RỖNG cũng hiện '—'. */}
                MST {card.taxCode === null || card.taxCode === '' ? '—' : card.taxCode} · {card.phone === null || card.phone === '' ? '—' : card.phone}
              </span>
            )}
            <button type="button" onClick={() => { void logout().then(() => { location.assign('/ncc') }) }}
                    className="text-xs font-semibold bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5">
              {t('ncc.logout')}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4">
        {noVendor ? (
          <Card className="p-6 text-center text-slate-500">{t('ncc.noVendorHint')}</Card>
        ) : (
          <>
            {/* ⚠ `data-live` = VÙNG ĐỔI THEO DỮ LIỆU THẬT. Ảnh đối chiếu `specs/screenshots/D03/ncc.png`
                ẩn đúng những vùng này trước khi chụp (`e2e/menu.spec.ts`). Thêm vùng động mới ở màn
                này thì gắn luôn `data-live`. */}
            <div data-live className="grid grid-cols-3 gap-2 mb-4">
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center">
                <div className="text-lg font-bold text-amber-600 tabular-nums">{pending}</div>
                <div className="text-[10px] font-semibold text-amber-700 uppercase">{t('ncc.kpiPending')}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-center">
                <div className="text-lg font-bold text-slate-700 tabular-nums">{legs.length}</div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase">{t('ncc.kpiTotal')}</div>
              </div>
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center">
                <div className="text-lg font-bold text-red-600 tabular-nums">
                  {recon === null ? '—' : formatVND(toMoney(recon.summary.dueTotal))}
                </div>
                <div className="text-[10px] font-semibold text-red-700 uppercase">{t('ncc.kpiDue')}</div>
              </div>
            </div>

            <div className="flex gap-2 mb-3 flex-wrap" role="tablist">
              {TABS.filter((id) => tabShown(id)).map((id) => (
                <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => { setTab(id) }}
                        className={`px-3 py-2 rounded-lg text-sm font-bold transition ${tab === id
                          ? 'bg-brand-500 text-white shadow'
                          : 'bg-white border border-slate-200 text-slate-600'}`}>
                  {tabLabel[id]}
                </button>
              ))}
            </div>

            {(err ?? loadErr) !== null && (
              <div data-live role="alert" className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {err ?? loadErr}
              </div>
            )}

            <div data-live>
              {tab === 'plan' && <PlanTab legs={legs} planned={planned ?? []} onChanged={reloadPlanned} />}
              {tab === 'orders' && (rows === null && loadErr === null
                ? <p>{t('common.loading')}</p>
                : <OrdersTab legs={legs} run={run} onReject={(l) => { setRejectOf(l); setReason('') }} />)}
              {tab === 'bidding' && <BiddingTab vendor={card} />}
              {tab === 'recon' && <ReconTab />}
              {tab === 'report' && <ReportTab legs={legs} vendorName={card?.vendorName ?? me?.name ?? ''} />}
            </div>
          </>
        )}
      </main>

      {rejectOf !== null && (
        <Modal title={t('ncc.rejTitle')} onClose={() => { setRejectOf(null) }} className="max-w-md">
          {/* Câu hướng dẫn + ô lý do BA DÒNG — đúng bản cũ (`NccPortal.tsx:413`): lý do từ chối là
              thứ điều hành đọc để xếp đối tác khác, một dòng `Input` không đủ chỗ viết. */}
          <p className="text-[12px] text-slate-600 mb-2">{t('ncc.rejHint')}</p>
          <textarea value={reason} aria-label={t('ncc.rejPh')} rows={3} placeholder={t('ncc.rejPh')} autoFocus
                    onChange={(e) => { setReason(e.target.value) }}
                    className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-[12px] w-full" />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => { setRejectOf(null) }}>{t('ncc.cancel')}</Button>
            <Button disabled={reason.trim() === ''}
                    onClick={() => { void run(() => rejectMyLegApi(rejectOf.id, rejectOf.version, reason.trim())) }}>
              {t('ncc.rejOk')}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
